import { FormItem, Textarea } from '@vkontakte/vkui';
import { useState } from 'react';
import { api, errorMessage } from '../../api/client';
import { useSession } from '../../api/session';
import { ROLE_LABELS, type UserRole } from '../../api/types';
import { t } from '../../i18n';
import { ExternalIcon } from '../../shared/icons';
import { formatDate } from '../../shared/lib/date';
import {
  Action,
  CardHeading,
  ExternalLink,
  FactGrid,
  FieldLabel,
  Gap,
  Gutter,
  Meta,
  Muted,
  NoticeBlock,
  Section,
  SectionTitle,
  Select,
  StatusBadge,
  type Fact,
} from '../../shared/ui';
import {
  ConfirmDialog,
  canAct,
  formatDateTime,
  personName,
  vkProfileUrl,
} from '../review/staffKit';
import type { AdminUser } from './AdminPanel';

/**
 * Карточка участника: роль, заметка и право записи.
 *
 * Отдельного маршрута GET /api/users/:id на сервере нет — есть только
 * список. Поэтому строка приходит пропсом из списка, а каждое действие
 * возвращает обновлённую строку, которой мы и заменяем свою копию: лишний
 * запрос за тем, что сервер уже прислал, здесь не нужен.
 */

const ROLE_OPTIONS = (Object.keys(ROLE_LABELS) as UserRole[]).map((value) => ({
  value,
  label: ROLE_LABELS[value],
}));

/** Строка appUser без вычисляемых полей списка. */
type UserRow = AdminUser;

interface PendingAction {
  key: 'revoke' | 'restore';
  title: string;
  text: string;
  confirmText: string;
  destructive?: boolean;
}

export function AdminUserCard({
  user,
  onChanged,
}: {
  user: AdminUser;
  onChanged: (next: AdminUser) => void;
}) {
  const session = useSession();

  const [role, setRole] = useState<UserRole>(user.role);
  const [note, setNote] = useState(user.note ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingAction | null>(null);

  const writable = canAct(session);
  const isSelf = session.user?.id === user.id;

  /*
    Уходит только изменённое: свою роль сервер менять не даёт, и послать её
    нетронутой значило бы получить отказ вместо сохранённой заметки.
  */
  const roleDirty = !isSelf && role !== user.role;
  const noteDirty = note !== (user.note ?? '');
  const roleChanged = roleDirty || noteDirty;
  const roleBody: Record<string, unknown> = {
    ...(roleDirty ? { role } : null),
    ...(noteDirty ? { note } : null),
  };
  const savedMessage =
    roleDirty && noteDirty
      ? t('user.card.saved.roleAndNote')
      : roleDirty
        ? t('user.card.saved.role', { role: ROLE_LABELS[role] })
        : t('user.card.saved.note');

  const facts: Fact[] = [
    {
      // Ссылкой на самом номере, а не подписью под фактами: номер и есть
      // адрес страницы, и отдельная строчка «Профиль ВК» повторяла его же.
      label: t('user.card.fact.vkId'),
      value: (
        <ExternalLink href={vkProfileUrl(user.vkUserId)}>
          {user.vkUserId} <ExternalIcon tone="accent" />
        </ExternalLink>
      ),
    },
    // Прочерк вместо пропуска: «доступ снят — —» говорит ровно то же, что
    // значок над фактами, и клетка не прыгает, когда доступ снимают.
    { label: t('user.card.fact.revokedAt'), value: formatDateTime(user.revokedAt) },
    // Отметка редкая, поэтому клетка только пока она стоит; возврат доступа её снимает.
    {
      label: t('user.card.fact.deletedAt'),
      value: user.deletedAt === null ? null : formatDate(user.deletedAt),
    },
    { label: t('user.card.fact.createdAt'), value: formatDateTime(user.createdAt) },
    {
      label: t('user.card.fact.registryProfile'),
      value: user.hideVkProfile
        ? t('user.card.fact.registryHidden')
        : t('user.card.fact.registryVisible'),
    },
  ];

  function apply(row: UserRow, message: string) {
    onChanged({ ...user, ...row });
    setSaved(message);
    setError(null);
  }

  async function patch(body: Record<string, unknown>, message: string) {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      const row = await api.patch<UserRow>(`/api/users/${user.id}`, body);
      apply(row, message);
    } catch (cause: unknown) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function runPending() {
    if (!pending) return;
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      if (pending.key === 'revoke') {
        const result = await api.post<{ user: UserRow }>(`/api/users/${user.id}/revoke`);
        apply(result.user, t('user.card.saved.revoked'));
      } else {
        const row = await api.post<UserRow>(`/api/users/${user.id}/restore`);
        apply(row, t('user.card.saved.restored'));
      }
      setPending(null);
    } catch (cause: unknown) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Section>
        <Gutter>
          <CardHeading
            badgeAt="title"
            badge={
              <StatusBadge tone={user.canWrite ? 'published' : 'rejected'}>
                {user.canWrite ? t('common.access.granted') : t('user.state.revoked')}
              </StatusBadge>
            }
          >
            {personName(user)}
          </CardHeading>

          <Gap step="s2" />
          {/* Роль лиловым, как в макете: это единственное, что отличает
              персонал от участника, и серым она терялась среди фактов. */}
          <Meta tone="lilac">{[ROLE_LABELS[user.role], isSelf ? t('user.card.self') : null]}</Meta>

          <Gap step="s4" />
          <FactGrid facts={facts} />
        </Gutter>
      </Section>

      {saved ? <NoticeBlock tone="success">{saved}</NoticeBlock> : null}
      {error ? (
        <NoticeBlock tone="error" title={t('user.card.saveError')}>
          {error}
        </NoticeBlock>
      ) : null}

      <Section>
        <SectionTitle>{t('user.card.section.roleAndNote')}</SectionTitle>

        <FormItem
          top={
            <FieldLabel hint={isSelf ? t('user.card.hint.roleSelf') : t('user.card.hint.role')}>
              {t('user.field.role')}
            </FieldLabel>
          }
        >
          <Select
            value={role}
            disabled={busy || !writable || isSelf}
            options={ROLE_OPTIONS}
            onChange={(event) => setRole(event.target.value as UserRole)}
          />
        </FormItem>

        <FormItem
          top={<FieldLabel hint={t('user.card.hint.note')}>{t('common.field.note')}</FieldLabel>}
        >
          <Textarea
            value={note}
            rows={3}
            disabled={busy || !writable}
            onChange={(event) => setNote(event.target.value)}
          />
        </FormItem>

        {/* Кнопка одна на оба поля: сервер принимает роль и заметку одним
            PATCH, и две кнопки рядом заставляли выбирать, что сохранять
            раньше. Уходит только изменённое — своя роль серверу запрещена, и
            слать её неизменной значило бы нарваться на отказ. */}
        <Gutter>
          <Action
            tone="accent"
            size="m"
            stretched
            disabled={busy || !writable || !roleChanged}
            onClick={() => void patch(roleBody, savedMessage)}
          >
            {t('common.action.save')}
          </Action>
        </Gutter>
      </Section>

      <Section>
        <SectionTitle accent="pink">{t('user.card.section.access')}</SectionTitle>
        <Gutter>
          <Muted>{t('user.card.accessNote')}</Muted>
          <Gap step="s2h" />

          {user.canWrite ? (
            <Action
              tone="action"
              size="m"
              stretched
              disabled={busy || !writable || isSelf}
              onClick={() =>
                setPending({
                  key: 'revoke',
                  title: t('user.card.confirm.revoke.title'),
                  text: t('user.card.confirm.revoke.text'),
                  confirmText: t('user.card.action.revoke'),
                  destructive: true,
                })
              }
            >
              {t('user.card.action.revoke')}
            </Action>
          ) : (
            <Action
              tone="accent"
              size="m"
              stretched
              disabled={busy || !writable}
              onClick={() =>
                setPending({
                  key: 'restore',
                  title: t('user.card.confirm.restore.title'),
                  text:
                    user.deletedAt === null
                      ? t('user.card.confirm.restore.text')
                      : t('user.card.confirm.restore.textDeleted'),
                  confirmText: t('user.card.action.restore'),
                })
              }
            >
              {t('user.card.action.restore')}
            </Action>
          )}

          {isSelf && user.canWrite ? (
            <>
              <Gap step="s2" />
              <Muted>{t('user.card.selfRevokeNote')}</Muted>
            </>
          ) : null}
        </Gutter>
      </Section>

      {pending ? (
        <ConfirmDialog
          title={pending.title}
          text={pending.text}
          confirmText={pending.confirmText}
          destructive={pending.destructive}
          busy={busy}
          error={error}
          onCancel={() => setPending(null)}
          onConfirm={() => void runPending()}
        />
      ) : null}
    </>
  );
}
